import { EMPTY, Observable, catchError, finalize, merge, mergeMap, of, pipe, share, switchMap, tap } from 'rxjs';

import { computed, inject } from '@angular/core';

import {
  RequestStatusState,
  setCompletedState,
  setErrorState,
  setPendingState,
  withRequestStatus,
} from '@mitsubishi/utils';

import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';

import { StudyDetailDto, StudyEventDto, StudyListItemDto, StudyUnitDetailDto } from '../models/study-next.model';
import { StudyQueryService } from '../services/study-query.service';
import { StudyNextTranslator } from '../utils/study-next.translator';
import { PatchOp, removedStudyIds, touchedStudyIds } from '../ws/apply-patch/study-change';

/** A unit as `GET …/unit/{unitId}` returns it, remembered with the study it was fetched under. */
export type CachedStudyUnit = StudyUnitDetailDto & { studyId: string };

type ProjectStudiesState = {
  projectId: string | null;
  /** `/studies`: the project's permanent studies — skeleton and counters, never units. */
  summaries: StudyListItemDto[];
  /** `/studies/{studyId}`: subcategories and working conditions, never units. */
  detailById: Record<string, StudyDetailDto>;
  unitById: Record<string, CachedStudyUnit>;
};

const initialState: ProjectStudiesState = {
  projectId: null,
  summaries: [],
  detailById: {},
  unitById: {},
};

/**
 * The project's studies for everything outside the paginated grid once the project GET stops embedding
 * them (`?studies=none`, PLAN.md P1): what the project page lists, what the shared study-level and
 * unit-level pages read through `StudyFacade`. Root-provided because it backs a root facade; its
 * lifecycle is the project's — `load(projectId)` resets it, as `ProjectState.current` is replaced.
 *
 * State is the transport shape. `StoreStudyReadSource` adapts it to the legacy models the consumers are
 * typed against, at that boundary and nowhere else.
 *
 * The loading methods return cold observables that patch state on success, because their callers need
 * completion: route resolvers wait for them, and the study page forkJoins them with its own reads. A
 * refresh is deduplicated while in flight, so a patch-driven refresh and a page-driven one of the same
 * study share one request.
 */
export const ProjectStudiesStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withRequestStatus(),
  withComputed(({ summaries }) => ({
    studySummaries: computed(() => summaries().map(item => StudyNextTranslator.toStudySummary(item))),
  })),
  withMethods((store, service = inject(StudyQueryService)) => {
    const inFlight = new Map<string, Observable<unknown>>();

    /** One request per key at a time; a caller arriving while it is pending joins it. */
    const dedupe = <T>(key: string, request: () => Observable<T>): Observable<T> => {
      const pending = inFlight.get(key) as Observable<T> | undefined;
      if (pending) {
        return pending;
      }
      const shared = request().pipe(
        finalize(() => inFlight.delete(key)),
        share()
      );
      inFlight.set(key, shared);
      return shared;
    };

    /** Writes only while the response still belongs to the project it was requested for — `load` may have moved on. */
    const patchFor = (projectId: string, patch: () => Partial<ProjectStudiesState & RequestStatusState>): void => {
      if ((store.projectId() ?? '') === projectId) {
        patchState(store, patch());
      }
    };

    const refreshStudy = (studyId: string): Observable<StudyDetailDto> => {
      const projectId = store.projectId() ?? '';
      return dedupe(`study:${studyId}`, () =>
        service
          .getStudy(projectId, studyId)
          .pipe(
            tap(detail => patchFor(projectId, () => ({ detailById: { ...store.detailById(), [studyId]: detail } })))
          )
      );
    };

    const refreshUnit = (studyId: string, unitId: string): Observable<StudyUnitDetailDto> => {
      const projectId = store.projectId() ?? '';
      return dedupe(`unit:${unitId}`, () =>
        service
          .getStudyUnit(projectId, studyId, unitId)
          .pipe(
            tap(detail =>
              patchFor(projectId, () => ({ unitById: { ...store.unitById(), [unitId]: { ...detail, studyId } } }))
            )
          )
      );
    };

    const refreshSummaries = rxMethod<void>(
      pipe(
        switchMap(() =>
          service.getStudies(store.projectId() ?? '').pipe(
            tapResponse({
              next: summaries => patchState(store, { summaries }),
              // A failed background refresh keeps the last good list; the next patch retries.
              error: () => undefined,
            })
          )
        )
      )
    );

    const refreshHeldUnits = (studyId: string): Observable<unknown> => {
      const held: Observable<unknown>[] = [];
      for (const [unitId, unit] of Object.entries(store.unitById())) {
        if (unit.studyId === studyId) {
          held.push(refreshUnit(studyId, unitId));
        }
      }
      // Each request fails alone: one 404 must not cancel the others.
      return merge(...held.map(request => request.pipe(catchError(() => EMPTY))));
    };

    const refreshHeldStudy = (studyId: string): Observable<unknown> =>
      merge(
        store.detailById()[studyId] ? refreshStudy(studyId).pipe(catchError(() => EMPTY)) : EMPTY,
        refreshHeldUnits(studyId)
      );

    const dropStudies = (studyIds: string[]): void => {
      const dropped = new Set(studyIds);
      patchState(store, {
        detailById: Object.fromEntries(Object.entries(store.detailById()).filter(([studyId]) => !dropped.has(studyId))),
        unitById: Object.fromEntries(Object.entries(store.unitById()).filter(([, unit]) => !dropped.has(unit.studyId))),
      });
    };

    return {
      /** Forgets the previous project and lists this one's studies. Emits once the list is in state. */
      load(projectId: string): Observable<StudyListItemDto[]> {
        inFlight.clear();
        patchState(store, { ...initialState, projectId, ...setPendingState() });
        return service.getStudies(projectId).pipe(
          tap({
            next: summaries => patchFor(projectId, () => ({ summaries, ...setCompletedState() })),
            error: () => patchFor(projectId, () => setErrorState('study-list')),
          })
        );
      },

      ensureStudy(studyId: string): Observable<StudyDetailDto> {
        const held = store.detailById()[studyId];
        return held ? of(held) : refreshStudy(studyId);
      },

      refreshStudy,

      ensureUnit(studyId: string, unitId: string): Observable<StudyUnitDetailDto> {
        const held = store.unitById()[unitId];
        return held ? of(held) : refreshUnit(studyId, unitId);
      },

      refreshSummaries,

      /**
       * The project's JSON patches read as invalidation, never as state (PAGINATION.md §7): any study op
       * refetches the list; a removed study is dropped; every other touched study that is held is
       * refetched — detail and visited units.
       */
      onStudyOps: rxMethod<readonly PatchOp[]>(
        pipe(
          mergeMap(ops => {
            refreshSummaries();
            const removed = removedStudyIds(ops);
            dropStudies(removed);
            const touched = touchedStudyIds(ops) ?? Object.keys(store.detailById());
            return merge(...touched.filter(studyId => !removed.includes(studyId)).map(refreshHeldStudy));
          })
        )
      ),

      /**
       * Nothing held here follows the persisted filters, and the detail follows the unit set (it decides the
       * tabs), not a unit update. An external unit's event names its parent, the unit its page holds.
       */
      onStudyEvent: rxMethod<StudyEventDto>(
        pipe(
          mergeMap(event => {
            if (event.type === 'PERFORMANCE_FILTER_CHANGED') {
              return EMPTY;
            }
            refreshSummaries();
            const studyId = event.studyId;

            switch (event.type) {
              case 'STUDY_CREATED':
                return EMPTY;
              case 'STUDY_DELETED':
                dropStudies([studyId]);
                return EMPTY;
              case 'UNITS_ADDED':
              case 'UNITS_REMOVED':
              case 'UNITS_UPDATED': {
                const unitIds = event.unitIds ?? [];
                if (event.type === 'UNITS_REMOVED') {
                  const removed = new Set(unitIds);
                  patchState(store, {
                    unitById: Object.fromEntries(
                      Object.entries(store.unitById()).filter(([unitId]) => !removed.has(unitId))
                    ),
                  });
                }
                const held: Observable<unknown>[] =
                  event.type !== 'UNITS_UPDATED' && store.detailById()[studyId] ? [refreshStudy(studyId)] : [];
                for (const unitId of unitIds) {
                  if (store.unitById()[unitId]?.studyId === studyId) {
                    held.push(refreshUnit(studyId, unitId));
                  }
                }
                // Each request fails alone: one 404 must not cancel the others.
                return merge(...held.map(request => request.pipe(catchError(() => EMPTY))));
              }
              case 'PERFORMANCES_RECALCULATED':
                return refreshHeldUnits(studyId);
              default:
                return refreshHeldStudy(studyId);
            }
          })
        )
      ),
    };
  })
);
